import React from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Badge,
  Button,
  Placeholder,
  Spinner,
} from "react-bootstrap";
import { HeadPage } from "src/componentes";
import CTeVisualizaCode from "./CTe.Visualiza.Code";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faDownload,
  faPrint,
  faCopy,
  faTruck,
  faRoute,
  faUsers,
  faBoxes,
  faFileInvoice,
  faDollarSign,
  faInfoCircle,
  faReceipt,
} from "@fortawesome/free-solid-svg-icons";

const CTeVisualizaView = () => {
  const code = CTeVisualizaCode();
  const cte = code.cte;

  if (code.loading) {
    return (
      <Container className="py-4">
        <HeadPage headText="Visualizar CT-e" />
        <Placeholder animation="glow" className="mt-4">
          <Placeholder xs={12} size="lg" className="mb-3" />
          <Placeholder xs={12} className="mb-2" />
          <Placeholder xs={12} className="mb-2" />
          <Placeholder xs={12} className="mb-2" />
        </Placeholder>
      </Container>
    );
  }

  if (!cte) {
    return (
      <Container className="py-4">
        <HeadPage headText="Visualizar CT-e" />
        <Card className="mt-4 text-center p-5 border shadow-sm">
          <Card.Body>
            <h4 className="text-body mb-3">CT-e não encontrado</h4>
            <p className="text-body-secondary mb-4">
              Não foi possível carregar as informações deste Conhecimento de Transporte.
            </p>
            <Button variant="outline-primary" onClick={code.onVoltar}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Voltar para Listagem
            </Button>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  const isAutorizado = cte.autorizacao?.codigoStatusResposta === 100;

  return (
    <Container className="pb-5 container-print">
      {/* Cabeçalho superior da página */}
      <div className="no-print">
        <HeadPage headText={`CT-e: ${cte.numeroCte} / Série: ${cte.serie}`} />

        {/* Barra de Ações Rápidas */}
        <Card className="my-3 border shadow-sm">
          <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-2 p-3">
            <Button variant="outline-secondary" size="sm" onClick={code.onVoltar}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Voltar
            </Button>
            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={code.onBaixarXml}
                disabled={code.downloadingXml || !cte.id}
              >
                {code.downloadingXml ? (
                  <Spinner size="sm" animation="border" className="me-1" />
                ) : (
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                )}
                Baixar XML
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={code.onImprimir}
                disabled={code.printing || !cte.id}
              >
                {code.printing ? (
                  <Spinner size="sm" animation="border" className="me-1" />
                ) : (
                  <FontAwesomeIcon icon={faPrint} className="me-2" />
                )}
                Imprimir DACTE
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Status da Autorização / SEFAZ */}
      <Card className="mb-4 border shadow-sm">
        <Card.Header className="bg-body-tertiary py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            Status do Documento Fiscal
          </div>
          <div>
            {cte.autorizacao ? (
              <Badge
                bg={isAutorizado ? "success" : "warning"}
                className={`px-3 py-2 fs-6 ${!isAutorizado ? "text-dark" : ""}`}
              >
                {cte.autorizacao.codigoStatusResposta} -{" "}
                {cte.autorizacao.descricaoMotivoResposta || "Autorizado"}
              </Badge>
            ) : (
              <Badge bg="secondary" className="px-3 py-2 fs-6">
                Não transmitido
              </Badge>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col xs={12} sm={6} md={3} className="mb-2">
              <span className="text-body-secondary small d-block">Número CT-e</span>
              <strong className="fs-5 text-body">{cte.numeroCte}</strong>
            </Col>
            <Col xs={12} sm={6} md={2} className="mb-2">
              <span className="text-body-secondary small d-block">Série</span>
              <strong className="fs-5 text-body">{cte.serie}</strong>
            </Col>
            <Col xs={12} sm={6} md={3} className="mb-2">
              <span className="text-body-secondary small d-block">Data/Hora Emissão</span>
              <strong className="text-body">
                {cte.emissaoDataHora
                  ? code.FormatarDataHoraDefault(cte.emissaoDataHora)
                  : "-"}
              </strong>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-body-secondary small d-block">Status de Envio</span>
              <span className="text-body">
                {cte.autorizacao
                  ? `${cte.autorizacao.codigoStatusEnvio} - ${cte.autorizacao.descricaoStatusEnvio}`
                  : "-"}
              </span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Características & Modal do Transporte */}
      <Card className="mb-4 border shadow-sm">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faTruck} className="me-2" />
            Características e Transporte
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-body-secondary small d-block">Tipo de CT-e</span>
              <strong className="text-body">{code.getTipoCteLabel(cte.tipoCte)}</strong>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-body-secondary small d-block">Tipo de Serviço</span>
              <strong className="text-body">{code.getTipoServicoLabel(cte.tipoServico)}</strong>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-body-secondary small d-block">Modal de Transporte</span>
              <strong className="text-body">{code.getModalLabel(cte.modal)}</strong>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-body-secondary small d-block">RNTRC</span>
              <span className="text-body">{cte.rntrc || "-"}</span>
            </Col>
            {cte.chaveCTeComplementado && (
              <Col xs={12} className="mb-2">
                <span className="text-body-secondary small d-block">
                  Chave do CT-e Complementado
                </span>
                <span className="text-body">{cte.chaveCTeComplementado}</span>
              </Col>
            )}
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-body-secondary small d-block">Retira</span>
              <span className="text-body">{cte.retira === 0 ? "Sim" : "Não"}</span>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-body-secondary small d-block">Tipo de Tomador</span>
              <strong className="text-body">{code.getTipoTomadorLabel(cte.tipoTomador)}</strong>
            </Col>
            {Boolean(cte.tributacaoId || code.tributacaoNome) && (
              <Col xs={12} sm={6} md={4} className="mb-2">
                <span className="text-body-secondary small d-block">Tributação Vinculada</span>
                <strong className="text-body">{code.tributacaoNome || `Cód. ${cte.tributacaoId}`}</strong>
              </Col>
            )}
            {cte.cfop && (
              <Col xs={12} sm={6} md={4} className="mb-2">
                <span className="text-body-secondary small d-block">CFOP</span>
                <span className="text-body">{cte.cfop}</span>
              </Col>
            )}
            {cte.naturezaOperacao && (
              <Col xs={12} sm={6} md={4} className="mb-2">
                <span className="text-body-secondary small d-block">Natureza da Operação</span>
                <span className="text-body">{cte.naturezaOperacao}</span>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* Percurso do Transporte */}
      <Card className="mb-4 border shadow-sm">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faRoute} className="me-2" />
            Percurso da Prestação
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col xs={12} md={6} className="mb-3">
              <div className="p-3 border rounded bg-body-tertiary h-100">
                <div className="text-body-secondary small fw-bold text-uppercase mb-1">
                  Início da Prestação (Origem)
                </div>
                <div className="fs-5 fw-bold text-body">
                  {cte.nomeMunicipioInicio} - {cte.ufInicio}
                </div>
                <div className="text-body-secondary small">
                  Código IBGE: {cte.codigoMunicipioInicio || "-"}
                </div>
              </div>
            </Col>
            <Col xs={12} md={6} className="mb-3">
              <div className="p-3 border rounded bg-body-tertiary h-100">
                <div className="text-body-secondary small fw-bold text-uppercase mb-1">
                  Término da Prestação (Destino)
                </div>
                <div className="fs-5 fw-bold text-body">
                  {cte.nomeMunicipioFim} - {cte.ufFim}
                </div>
                <div className="text-body-secondary small">
                  Código IBGE: {cte.codigoMunicipioFim || "-"}
                </div>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Atores / Participantes */}
      <Card className="mb-4 border shadow-sm">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faUsers} className="me-2" />
            Participantes
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            {/* Remetente */}
            <Col xs={12} lg={6} className="mb-3">
              <div className="border rounded p-3 h-100 bg-body-tertiary">
                <h6 className="fw-bold text-primary border-bottom pb-2 mb-3">
                  Remetente
                </h6>
                <div className="mb-2">
                  <span className="text-body-secondary small d-block">Razão Social / Nome</span>
                  <strong className="text-body">{cte.remetente?.nome || "-"}</strong>
                </div>
                {cte.remetente?.nomeFantasia && (
                  <div className="mb-2">
                    <span className="text-body-secondary small d-block">Nome Fantasia</span>
                    <span className="text-body">{cte.remetente.nomeFantasia}</span>
                  </div>
                )}
                <Row className="mb-2">
                  <Col xs={6}>
                    <span className="text-body-secondary small d-block">CNPJ / CPF</span>
                    <span className="text-body">{code.formatarDocumento(cte.remetente?.documento)}</span>
                  </Col>
                  <Col xs={6}>
                    <span className="text-body-secondary small d-block">Inscrição Estadual</span>
                    <span className="text-body">{cte.remetente?.inscricaoEstadual || "ISENTO"}</span>
                  </Col>
                </Row>
                <div className="mb-2">
                  <span className="text-body-secondary small d-block">Telefone</span>
                  <span className="text-body">
                    {cte.remetente?.fone
                      ? code.FormatarTelefone(cte.remetente.fone)
                      : "-"}
                  </span>
                </div>
                <div>
                  <span className="text-body-secondary small d-block">Endereço</span>
                  <span className="text-body">
                    {cte.remetente
                      ? `${cte.remetente.endereco || ""}, ${
                          cte.remetente.enderecoNumero || "S/N"
                        } - ${cte.remetente.bairro || ""}, ${
                          cte.remetente.nomeMunicipio || ""
                        } - ${cte.remetente.uf || ""} - CEP: ${
                          cte.remetente.cep ? code.FormatarCEP(cte.remetente.cep) : "-"
                        }`
                      : "-"}
                  </span>
                </div>
              </div>
            </Col>

            {/* Destinatário */}
            <Col xs={12} lg={6} className="mb-3">
              <div className="border rounded p-3 h-100 bg-body-tertiary">
                <h6 className="fw-bold text-primary border-bottom pb-2 mb-3">
                  Destinatário
                </h6>
                <div className="mb-2">
                  <span className="text-body-secondary small d-block">Razão Social / Nome</span>
                  <strong className="text-body">{cte.destinatario?.nome || "-"}</strong>
                </div>
                {cte.destinatario?.nomeFantasia && (
                  <div className="mb-2">
                    <span className="text-body-secondary small d-block">Nome Fantasia</span>
                    <span className="text-body">{cte.destinatario.nomeFantasia}</span>
                  </div>
                )}
                <Row className="mb-2">
                  <Col xs={6}>
                    <span className="text-body-secondary small d-block">CNPJ / CPF</span>
                    <span className="text-body">{code.formatarDocumento(cte.destinatario?.documento)}</span>
                  </Col>
                  <Col xs={6}>
                    <span className="text-body-secondary small d-block">Inscrição Estadual</span>
                    <span className="text-body">{cte.destinatario?.inscricaoEstadual || "ISENTO"}</span>
                  </Col>
                </Row>
                <div className="mb-2">
                  <span className="text-body-secondary small d-block">Telefone</span>
                  <span className="text-body">
                    {cte.destinatario?.fone
                      ? code.FormatarTelefone(cte.destinatario.fone)
                      : "-"}
                  </span>
                </div>
                <div>
                  <span className="text-body-secondary small d-block">Endereço</span>
                  <span className="text-body">
                    {cte.destinatario
                      ? `${cte.destinatario.endereco || ""}, ${
                          cte.destinatario.enderecoNumero || "S/N"
                        } - ${cte.destinatario.bairro || ""}, ${
                          cte.destinatario.nomeMunicipio || ""
                        } - ${cte.destinatario.uf || ""} - CEP: ${
                          cte.destinatario.cep
                            ? code.FormatarCEP(cte.destinatario.cep)
                            : "-"
                        }`
                      : "-"}
                  </span>
                </div>
              </div>
            </Col>

            {/* Tomador (se informado como entidade separada) */}
            {cte.tomador && (
              <Col xs={12} className="mt-2">
                <div className="border rounded p-3 bg-body-tertiary">
                  <h6 className="fw-bold text-primary border-bottom pb-2 mb-3">
                    Tomador do Serviço
                  </h6>
                  <Row>
                    <Col xs={12} md={6} className="mb-2">
                      <span className="text-body-secondary small d-block">Razão Social / Nome</span>
                      <strong className="text-body">{cte.tomador.nome}</strong>
                    </Col>
                    <Col xs={12} md={6} className="mb-2">
                      <span className="text-body-secondary small d-block">CNPJ / CPF</span>
                      <span className="text-body">{code.formatarDocumento(cte.tomador.documento)}</span>
                    </Col>
                  </Row>
                </div>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* Informações da Carga */}
      <Card className="mb-4 border shadow-sm">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faBoxes} className="me-2" />
            Informações da Carga
          </div>
        </Card.Header>
        <Card.Body>
          <Row className="mb-3">
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-body-secondary small d-block">Valor Total da Carga</span>
              <strong className="fs-5 text-success">
                {code.formatarMoeda(cte.carga?.valorCarga)}
              </strong>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-body-secondary small d-block">Produto Predominante</span>
              <strong className="text-body">{cte.carga?.produtoPredominante || "-"}</strong>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-body-secondary small d-block">Outras Características</span>
              <span className="text-body">{cte.carga?.outraCaracteristica || "-"}</span>
            </Col>
          </Row>

          <h6 className="fw-bold text-primary mt-3 mb-2">Quantidades e Medidas</h6>
          <div className="table-responsive">
            <Table hover size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Tipo de Medida</th>
                  <th>Unidade</th>
                  <th className="text-end">Quantidade</th>
                </tr>
              </thead>
              <tbody>
                {cte.carga?.quantidades && cte.carga.quantidades.length > 0 ? (
                  cte.carga.quantidades.map((qtd, index) => (
                    <tr key={index}>
                      <td className="text-body">{qtd.tipoMedida}</td>
                      <td className="text-body">{code.getUnidadeMedidaLabel(qtd.codigoUnidadeMedida)}</td>
                      <td className="text-end fw-bold text-body">
                        {Number(qtd.quantidadeCarga).toLocaleString("pt-BR", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 4,
                        })}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="text-center text-body-secondary py-2">
                      Nenhuma quantidade informada.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Documentos Originários */}
      <Card className="mb-4 border shadow-sm">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
            Documentos Originários
          </div>
        </Card.Header>
        <Card.Body>
          <div className="table-responsive">
            <Table hover size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: "20%" }}>Tipo Documento</th>
                  <th>Chave de Acesso / Identificador</th>
                  <th style={{ width: "10%" }} className="text-center">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {cte.documentos && cte.documentos.length > 0 ? (
                  cte.documentos.map((doc, index) => (
                    <tr key={index}>
                      <td className="text-body">{code.getTipoDocumentoLabel(doc.tipoDocumento)}</td>
                      <td className="font-monospace text-break text-body">
                        {doc.chaveDocumento}
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          title="Copiar Chave"
                          onClick={() =>
                            code.copiarTexto(doc.chaveDocumento, "Chave")
                          }
                        >
                          <FontAwesomeIcon icon={faCopy} />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="text-center text-body-secondary py-2">
                      Nenhum documento originário vinculado.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Valores da Prestação e Componentes */}
      <Card className="mb-4 border shadow-sm">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faDollarSign} className="me-2" />
            Valores da Prestação do Serviço
          </div>
        </Card.Header>
        <Card.Body>
          {/* Cards de Totais */}
          <Row className="g-3 mb-4">
            <Col xs={12} md={4}>
              <div className="p-3 border rounded text-center bg-body-tertiary h-100">
                <span className="text-body-secondary small d-block fw-semibold">
                  VALOR DA PRESTAÇÃO
                </span>
                <span className="fs-4 fw-bold text-body mt-1 d-block">
                  {code.formatarMoeda(
                    cte.valoresPrestacao?.valorPrestacaoServico
                  )}
                </span>
              </div>
            </Col>
            <Col xs={12} md={4}>
              <div className="p-3 border rounded text-center bg-body-tertiary h-100">
                <span className="text-body-secondary small d-block fw-semibold">
                  VALOR TOTAL DOS TRIBUTOS
                </span>
                <span className="fs-4 fw-bold text-body mt-1 d-block">
                  {code.formatarMoeda(cte.valoresPrestacao?.valorTotalTributos)}
                </span>
              </div>
            </Col>
            <Col xs={12} md={4}>
              <div className="p-3 border border-success rounded text-center bg-body-tertiary h-100">
                <span className="text-success small fw-bold d-block">
                  VALOR TOTAL A RECEBER
                </span>
                <span className="fs-3 fw-bold text-success mt-1 d-block">
                  {code.formatarMoeda(cte.valoresPrestacao?.valorReceber)}
                </span>
              </div>
            </Col>
          </Row>

          <h6 className="fw-bold text-primary mb-2">
            Componentes da Prestação do Serviço
          </h6>
          <div className="table-responsive">
            <Table hover size="sm" className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Nome do Componente</th>
                  <th className="text-end" style={{ width: "25%" }}>
                    Valor (R$)
                  </th>
                </tr>
              </thead>
              <tbody>
                {cte.valoresPrestacao?.componentes &&
                cte.valoresPrestacao.componentes.length > 0 ? (
                  cte.valoresPrestacao.componentes.map((comp, index) => (
                    <tr key={index}>
                      <td className="text-body">{comp.nome}</td>
                      <td className="text-end fw-bold text-body">
                        {code.formatarMoeda(comp.valor)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2} className="text-center text-body-secondary py-2">
                      Nenhum componente cadastrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* Tributação e Impostos */}
      <Card className="mb-4 border shadow-sm">
        <Card.Header className="bg-body-tertiary py-2">
          <div className="fw-semibold text-primary d-flex align-items-center">
            <FontAwesomeIcon icon={faReceipt} className="me-2" />
            Tributação e Impostos
          </div>
        </Card.Header>
        <Card.Body>
          {/* Dados Gerais da Tributação */}
          <Row className="mb-3 pb-3 border-bottom">
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-body-secondary small d-block">Regra Tributária</span>
              <strong className="text-body">
                {code.tributacaoNome || (cte.tributacaoId ? `Cód. ${cte.tributacaoId}` : "-")}
              </strong>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-body-secondary small d-block">CFOP</span>
              <span className="text-body">{cte.cfop || "-"}</span>
            </Col>
            <Col xs={12} sm={6} md={4} className="mb-2">
              <span className="text-body-secondary small d-block">Natureza da Operação</span>
              <span className="text-body">{cte.naturezaOperacao || "-"}</span>
            </Col>
          </Row>

          <Row className="g-3">
            {/* ICMS */}
            <Col xs={12} lg={cte.ibscbs ? 6 : 12}>
              <div className="p-3 border rounded bg-body-tertiary h-100">
                <h6 className="fw-bold text-primary border-bottom pb-2 mb-3">
                  ICMS
                </h6>
                <div className="small">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-body-secondary">CST ICMS:</span>
                    <span className="fw-semibold text-body">
                      {code.getCstIcmsLabel(cte.icms?.cst)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-body-secondary">Base de Cálculo:</span>
                    <span className="text-body">
                      {code.formatarMoeda(cte.icms?.valorBaseCalculo)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-body-secondary">Alíquota ICMS:</span>
                    <span className="text-body">
                      {cte.icms?.percentualAliquota != null ? `${cte.icms.percentualAliquota}%` : "-"}
                    </span>
                  </div>
                  {Boolean(cte.icms?.percentualReducaoBaseCalculo) && (
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-body-secondary">Redução Base Cálculo:</span>
                      <span className="text-body">
                        {cte.icms?.percentualReducaoBaseCalculo}%
                      </span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between border-top pt-2 mt-2">
                    <span className="fw-bold text-body">Valor do ICMS:</span>
                    <span className="fw-bold fs-6 text-primary">
                      {code.formatarMoeda(cte.icms?.valorImposto)}
                    </span>
                  </div>
                </div>
              </div>
            </Col>

            {/* IBS / CBS (Reforma Tributária) */}
            {cte.ibscbs && (
              <Col xs={12} lg={6}>
                <div className="p-3 border rounded bg-body-tertiary border-info-subtle h-100">
                  <h6 className="fw-bold text-primary border-bottom pb-2 mb-3">
                    IBS / CBS (Reforma Tributária)
                  </h6>
                  <div className="small">
                    <Row className="g-2 mb-2">
                      <Col xs={6}>
                        <div className="d-flex justify-content-between">
                          <span className="text-body-secondary">CST IBS/CBS:</span>
                          <span className="fw-semibold text-body">
                            {cte.ibscbs.cst != null ? cte.ibscbs.cst : "-"}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="d-flex justify-content-between">
                          <span className="text-body-secondary">Class. Trib.:</span>
                          <span className="fw-semibold text-body font-monospace">
                            {cte.ibscbs.classTrib || cte.ibscbs.cClassifTrib || "-"}
                          </span>
                        </div>
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-body-secondary">Base de Cálculo IBS/CBS:</span>
                      <span className="text-body">
                        {code.formatarMoeda(cte.ibscbs.valorBC)}
                      </span>
                    </div>
                    <Row className="g-2 mb-2">
                      <Col xs={6}>
                        <div className="d-flex justify-content-between">
                          <span className="text-body-secondary">Alíq. CBS:</span>
                          <span className="text-body">
                            {cte.ibscbs.percentualCBS ?? 0}%
                          </span>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="d-flex justify-content-between">
                          <span className="text-body-secondary">Valor CBS:</span>
                          <span className="fw-bold text-body">
                            {code.formatarMoeda(cte.ibscbs.valorCBS)}
                          </span>
                        </div>
                      </Col>
                    </Row>
                    <Row className="g-2 mb-2">
                      <Col xs={6}>
                        <div className="d-flex justify-content-between">
                          <span className="text-body-secondary">IBS UF (Estadual):</span>
                          <span className="text-body">
                            {code.formatarMoeda(cte.ibscbs.valorUFIbs)} ({cte.ibscbs.percentualUFIBS ?? 0}%)
                          </span>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="d-flex justify-content-between">
                          <span className="text-body-secondary">IBS Município:</span>
                          <span className="text-body">
                            {code.formatarMoeda(cte.ibscbs.valorMunicipioIBS)} ({cte.ibscbs.percentualMunicipioIBS ?? 0}%)
                          </span>
                        </div>
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-between border-top pt-2 mt-2">
                      <span className="fw-bold text-body">Valor Total IBS:</span>
                      <span className="fw-bold fs-6 text-primary">
                        {code.formatarMoeda(cte.ibscbs.valorIBS)}
                      </span>
                    </div>
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {/* Informações Complementares / Observações */}
      {cte.complemento?.observacao && (
        <Card className="mb-4 border shadow-sm">
          <Card.Header className="bg-body-tertiary py-2">
            <div className="fw-semibold text-primary d-flex align-items-center">
              <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
              Observações / Informações Complementares
            </div>
          </Card.Header>
          <Card.Body>
            <div className="p-3 bg-body-tertiary rounded border font-monospace text-break text-body">
              {cte.complemento.observacao}
            </div>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default CTeVisualizaView;
